<script>
import Popper from '../utils/vue-popper'
import { addClass, removeClass, on, off } from '../utils/dom'
import { debounce, generateId } from '../utils/util'
import Vue from 'vue'

/**
 * Всплывающее подсказки
 * @displayName Tooltip
*/
export default {
  name: 'sTooltip',
  mixins: [Popper],
  props: {
    /**
     * Тема всплывающей подсказки
    */
    type: {
      type: String,
      validator: function (value) {
        return ['white', 'dark', 'error', 'transparent'].indexOf(value) !== -1
      },
      default: 'white'
    },
    border: {
      type: Boolean,
      default: true
    },
    /**
     * отображение содержимого, может быть переопределено с помощью `slot#content`
     */
    content: String,
    /**
     * задержка появления, в миллисекундах
     */
    openDelay: {
      type: Number,
      default: 0
    },
    /**
     * Отключена ли подсказка
     */
    disabled: Boolean,
    /**
     * следует ли управлять всплывающей подсказкой вручную. `mouseenter` и` mouseleave` не будут иметь эффекта, если установлено значение `true`
     */
    manual: Boolean,
    /**
     * пользовательское имя класса для всплывающей подсказки
     */
    popperClass: String,
    /**
     * Название анимации
     */
    transition: {
      type: String,
      default: 's-fade-in-enter'
    },
    /**
     * параметры [popper.js](https://popper.js.org/documentation.html)
     */
    popperOptions: {
      type: Object,
      default() {
        return {
          boundariesPadding: 10,
          gpuAcceleration: false
        }
      }
    },
    /**
     * может ли мышь войти во всплывающую подсказку
     */
    enterable: {
      type: Boolean,
      default: true
    },
    /**
     * таймаут в миллисекундах, чтобы скрыть всплывающую подсказку
     */
    hideAfter: {
      type: Number,
      default: 0
    },
    /**
     * [tabindex](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex) всплывающей подсказки
     */
    tabindex: {
      type: Number,
      default: null
    },
  },
  data() {
    return {
      tooltipId: `s-tooltip-${generateId()}`,
      timeoutPending: null,
      focusing: false
    }
  },
  beforeCreate() {
    if (this.$isServer) { return }

    this.popperVM = new Vue({
      data: function() {
        return { node: '' }
      },
      render() {
        return this.node
      }
    }).$mount()
    this.debounceClose = debounce(() => this.handleClosePopper(), 200)
  },
  render() {
    if (this.popperVM && (this.$slots.content || this.content)) {
      this.popperVM.node = (
        <transition
          name={ this.transition }
          onAfterLeave={ this.doDestroy }>
          <div
            onMouseleave={ () => { this.setExpectedState(false); this.debounceClose() } }
            onMouseenter= { () => { this.setExpectedState(true) } }
            ref="popper"
            role="tooltip"
            id={this.tooltipId}
            aria-hidden={ (this.disabled || !this.showPopper) ? 'true' : 'false' }
            v-show={!this.disabled && this.showPopper}
            class={
              ['s-tooltip__popper', 'is-' + this.type, this.popperClass, this.border ? 'is-border' : '']
            }>
            { this.$slots.content || this.content }
          </div>
        </transition>)
    }
    const firstElement = this.getFirstElement()
    if (!firstElement) { return null }

    const data = firstElement.data = firstElement.data || {}
    data.staticClass = this.addTooltipClass(data.staticClass)

    return firstElement
  },
  mounted() {
    this.referenceElm = this.$el
    if (this.$el.nodeType === 1) {
      this.$el.setAttribute('aria-describedby', this.tooltipId)
      if (this.tabindex) {
        this.$el.setAttribute('tabindex', this.tabindex)
      }
      on(this.referenceElm, 'mouseenter', this.show)
      on(this.referenceElm, 'mouseleave', this.hide)
      on(this.referenceElm, 'focus', () => {
        if (!this.$slots.default || !this.$slots.default.length) {
          this.handleFocus()
          return
        }
        const instance = this.$slots.default[0].componentInstance
        if (instance && instance.focus) {
          instance.focus()
        } else {
          this.handleFocus()
        }
      })
      on(this.referenceElm, 'blur', this.handleBlur)
      on(this.referenceElm, 'click', this.removeFocusing)
    }
    this._tooltipMutationObserver = new MutationObserver(() => {
      if (this.showPopper && this.referenceElm && !document.body.contains(this.referenceElm)) {
        this.doDestroy()
        this.showPopper = false
      }
    })
    this._tooltipMutationObserver.observe(document.body, { childList: true, subtree: true })
    if (this.value && this.popperVM) {
      this.popperVM.$nextTick(() => {
        if (this.value) {
          this.updatePopper()
        }
      })
    }
  },
  watch: {
    focusing(val) {
      if (val) {
        addClass(this.referenceElm, 'focusing')
      } else {
        removeClass(this.referenceElm, 'focusing')
      }
    },
    showPopper(val) {
      if (val && this.referenceElm && !document.body.contains(this.referenceElm)) {
        // referenceElm зник з DOM, прибираємо popper
        this.doDestroy()
        this.showPopper = false
      }
    }
  },
  methods: {
    show() {
      this.$emit('show')
      this.setExpectedState(true)
      this.handleShowPopper()
    },
    hide() {
      this.$emit('hide')
      this.setExpectedState(false)
      this.debounceClose()
    },
    handleFocus() {
      this.$emit('focus')
      this.focusing = true
      this.show()
    },
    handleBlur() {
      this.$emit('blur')
      this.focusing = false
      this.hide()
    },
    removeFocusing() {
      this.$emit('click')
      this.focusing = false
    },
    addTooltipClass(prev) {
      if (!prev) {
        return 's-tooltip'
      } else {
        return 's-tooltip ' + prev.replace('s-tooltip', '')
      }
    },
    handleShowPopper() {
      if (!this.expectedState || this.manual) { return }
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.showPopper = true
      }, this.openDelay)

      if (this.hideAfter > 0) {
        this.timeoutPending = setTimeout(() => {
          this.showPopper = false
        }, this.hideAfter)
      }
    },
    handleClosePopper() {
      if (this.enterable && this.expectedState || this.manual) { return }
      clearTimeout(this.timeout)

      if (this.timeoutPending) {
        clearTimeout(this.timeoutPending)
      }
      this.showPopper = false

      if (this.disabled) {
        this.doDestroy()
      }
    },
    setExpectedState(expectedState) {
      if (expectedState === false) {
        clearTimeout(this.timeoutPending)
      }
      this.expectedState = expectedState
    },
    getFirstElement() {
      const slots = this.$slots.default
      if (!Array.isArray(slots)) { return null }
      let element = null
      for (let index = 0; index < slots.length; index++) {
        if (slots[index] && slots[index].tag) {
          element = slots[index]
        }
      }
      return element
    }
  },
  beforeDestroy() {
    this.popperVM && this.popperVM.$destroy()
    if (this.popperVM && this.popperVM.$el && this.popperVM.$el.parentNode) {
      this.popperVM.$el.parentNode.removeChild(this.popperVM.$el)
    }
    if (this._tooltipMutationObserver) {
      this._tooltipMutationObserver.disconnect()
      this._tooltipMutationObserver = null
    }
  },
  unmounted() {
    const reference = this.referenceElm
    if (reference.nodeType === 1) {
      off(reference, 'mouseenter', this.show)
      off(reference, 'mouseleave', this.hide)
      off(reference, 'focus', this.handleFocus)
      off(reference, 'blur', this.handleBlur)
      off(reference, 'click', this.removeFocusing)
    }
  }
}
</script>
